import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

const TOKEN_KEY = 'aletheias_auth_token';

// Mirror the JWT into a cookie scoped to .aletheias.ai so other subdomains
// (notably aletheias.ai/viewer where OHIF is served) can read it.
// localStorage is per-origin and would otherwise be invisible cross-subdomain.
function setAuthCookie(value: string) {
  document.cookie = `${TOKEN_KEY}=${encodeURIComponent(value)}; Domain=.aletheias.ai; Path=/; Max-Age=${30 * 86400}; Secure; SameSite=Lax`;
}

function clearAuthCookie() {
  document.cookie = `${TOKEN_KEY}=; Domain=.aletheias.ai; Path=/; Max-Age=0; Secure; SameSite=Lax`;
}

export interface User {
  user_id: string;
  tenant_id: string | null;
  tenant_name: string | null;
  tenant_type: 'customer' | 'labeling_admin' | string | null;
  email: string;
  role: 'admin' | 'vet' | 'tech' | 'viewer' | string;
  products: string[];
  email_verified: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  const initialToken = localStorage.getItem(TOKEN_KEY);
  // Reseed the cross-subdomain cookie on store init for users who logged in
  // before this code shipped — they have a token in localStorage but no cookie.
  if (initialToken) setAuthCookie(initialToken);

  const token = ref<string | null>(initialToken);
  const user = ref<User | null>(null);
  const loading = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(
    () => user.value?.role === 'admin' || user.value?.tenant_type === 'labeling_admin'
  );
  const isLabelingAdmin = computed(() => user.value?.tenant_type === 'labeling_admin');
  const hasInsights = computed(() => user.value?.products?.includes('insights') ?? false);
  const hasVision = computed(() => user.value?.products?.includes('vision') ?? false);
  const fullName = computed(() => user.value?.email ?? '');

  function setToken(newToken: string | null) {
    token.value = newToken;
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
      setAuthCookie(newToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      clearAuthCookie();
    }
  }

  async function fetchMe(): Promise<boolean> {
    if (!token.value) return false;
    try {
      const resp = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token.value}` },
      });
      if (!resp.ok) {
        clear();
        return false;
      }
      user.value = (await resp.json()) as User;
      return true;
    } catch {
      clear();
      return false;
    }
  }

  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    try {
      const resp = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Login failed (${resp.status})`);
      }
      const data = (await resp.json()) as { access_token: string };
      setToken(data.access_token);
      const ok = await fetchMe();
      if (!ok) throw new Error('Logged in but failed to load profile');
    } finally {
      loading.value = false;
    }
  }

  async function forgotPassword(email: string): Promise<void> {
    const resp = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(body.detail || `Request failed (${resp.status})`);
    }
  }

  async function resetPassword(resetToken: string, newPassword: string): Promise<void> {
    loading.value = true;
    try {
      const resp = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password: newPassword }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Reset failed (${resp.status})`);
      }
      const data = (await resp.json()) as { access_token: string };
      setToken(data.access_token);
      await fetchMe();
    } finally {
      loading.value = false;
    }
  }

  function clear(): void {
    setToken(null);
    user.value = null;
  }

  function logout(): void {
    clear();
    window.location.assign('/login');
  }

  return {
    token, user, loading,
    isAuthenticated, isAdmin, isLabelingAdmin, hasInsights, hasVision, fullName,
    fetchMe, login, forgotPassword, resetPassword, logout, clear, setToken,
  };
});
